'use client';

import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _ArrowIosLeft, _ArrowIosRight;
import { useCallback, useEffect, useState } from 'react';
import ArrowIosLeft from '@crehana/compass.icons.outline/ArrowIosLeft';
import ArrowIosRight from '@crehana/compass.icons.outline/ArrowIosRight';
import { useTranslation } from '@crehana/i18n';
import { cva } from 'class-variance-authority';
import { CommandItem } from "../_shadcn-native-components/command";
import { Checkbox } from "../Checkbox";
import { cn } from "../utils";
import { useDropdownContext } from "./context";
import { DropdownBaseItem } from "./DropdownBaseItem";
import { DropdownBaseList } from "./DropdownBaseList";
import locales from "./i18n";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var ICON_VARIANTS = {
  "default": 'tw-bg-neutral-light-300 dark:tw-bg-neutral-dark-600 tw-text-neutral-light-700 dark:tw-text-neutral-dark-300 group-aria-selected:tw-bg-neutral-light-200 dark:group-aria-selected:tw-bg-neutral-dark-500',
  'default-selected': 'tw-text-primary-light-500 dark:tw-text-secondary-dark-500 dark:tw-bg-neutral-dark-700 dark:group-aria-selected:tw-bg-neutral-dark-700',
  primary: 'tw-text-primary-light-500 tw-bg-primary-light-100',
  'primary-selected': 'tw-text-primary-light-500 tw-bg-primary-light-100',
  success: 'tw-text-success-500 tw-bg-success-100 tw-bg-success-100',
  'success-selected': 'tw-text-success-500 tw-bg-success-100',
  alert: 'tw-text-alert-500 tw-bg-alert-100 tw-bg-alert-100',
  'alert-selected': 'tw-text-alert-500 tw-bg-alert-100',
  info: 'tw-text-info-500 tw-bg-info-100',
  'info-selected': 'tw-text-info-500 tw-bg-info-100',
  disabled: 'tw-text-neutral-light-300 dark:tw-text-neutral-dark-300'
};
var iconVariants = /*#__PURE__*/cva('tw-min-h-4 tw-min-w-4 tw-rounded-1 tw-flex tw-items-center tw-justify-center', {
  variants: {
    variant: ICON_VARIANTS
  },
  defaultVariants: {
    variant: 'default'
  }
});
var getParsedVariant = function getParsedVariant(_ref) {
  var _ref$iconColorVariant = _ref.iconColorVariant,
    iconColorVariant = _ref$iconColorVariant === void 0 ? 'default' : _ref$iconColorVariant,
    isSelected = _ref.isSelected;
  return isSelected ? "".concat(iconColorVariant, "-selected") : iconColorVariant;
};
export var DropdownLeveledList = function DropdownLeveledList(_ref2) {
  var _items$find2, _items$find3;
  var className = _ref2.className,
    items = _ref2.items,
    selected = _ref2.selected,
    onSelect = _ref2.onSelect,
    _ref2$isCheckbox = _ref2.isCheckbox,
    isCheckbox = _ref2$isCheckbox === void 0 ? true : _ref2$isCheckbox;
  var _useTranslation = useTranslation(),
    languageCode = _useTranslation.languageCode;
  var _useDropdownContext = useDropdownContext(),
    values = _useDropdownContext.values,
    setItems = _useDropdownContext.setItems,
    setValues = _useDropdownContext.setValues;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    selectedOption = _useState2[0],
    setSelectedOption = _useState2[1];
  var t = locales[languageCode];
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    setItems(items.map(function (item) {
      return _objectSpread(_objectSpread({}, item), {}, {
        type: 'option'
      });
    }));
  }, [items]);
  var selection = selected !== null && selected !== void 0 ? selected : values;
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var updateValues = useCallback(function (newValues) {
    setValues(newValues);
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (onSelect) onSelect(newValues);
  }, []);
  var handleSelection = function handleSelection(value, state) {
    var newSelection = isCheckbox ? state ? [].concat(_toConsumableArray(selection), [value]) : selection === null || selection === void 0 ? void 0 : selection.filter(function (item) {
      return item !== value;
    }) : state ? [value] : [];
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!isCheckbox && selectedOption) newSelection.push(selectedOption);
    updateValues(newSelection);
  };
  var handleClearCategory = function handleClearCategory() {
    var _items$find$items, _items$find;
    var subcategories = (_items$find$items = (_items$find = items.find(function (item) {
      return item.value === selectedOption;
    })) === null || _items$find === void 0 ? void 0 : _items$find.items) !== null && _items$find$items !== void 0 ? _items$find$items : [];
    if (subcategories.length > 0) {
      var newSelection = selection === null || selection === void 0 ? void 0 : selection.filter(function (item) {
        return !subcategories.map(function (subcategory) {
          return subcategory.value;
        }).includes(item);
      });
      updateValues(newSelection);
    }
  };
  return /*#__PURE__*/_jsx(DropdownBaseList, {
    selected: selected,
    className: className,
    children: selectedOption ? /*#__PURE__*/_jsxs("div", {
      className: "tw-relative tw-flex tw-flex-col",
      children: [/*#__PURE__*/_jsxs("button", {
        onClick: function onClick() {
          return setSelectedOption(undefined);
        },
        className: cn('tw-flex tw-items-center tw-gap-0.4 tw-sticky tw-top-0 tw-z-2', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'tw-w-full tw-text-left tw-py-0.8 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
        children: [_ArrowIosLeft || (_ArrowIosLeft = /*#__PURE__*/_jsx(ArrowIosLeft, {
          size: 2,
          className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
        })), t.DROPDOWN_OPTIONS_BACK_BUTTON]
      }), isCheckbox ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [(_items$find2 = items.find(function (item) {
          return item.value === selectedOption;
        })) === null || _items$find2 === void 0 ? void 0 : _items$find2.items.map(function (item) {
          var isSelected = selection === null || selection === void 0 ? void 0 : selection.includes(item.value);
          return /*#__PURE__*/_jsxs(CommandItem, {
            className: "tw-justify-start",
            value: item.value.toString(),
            removeCheck: true,
            onSelect: function onSelect() {
              return handleSelection(item.value, !isSelected);
            },
            children: [/*#__PURE__*/_jsx(Checkbox, {
              checked: isSelected
            }), item.label]
          }, item.value);
        }), /*#__PURE__*/_jsx("button", {
          onClick: handleClearCategory,
          className: cn('tw-w-full tw-sticky tw-bottom-0 tw-text-left tw-py-1.2 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
          children: t.DROPDOWN_OPTIONS_ERASE_ALL
        })]
      }) : (_items$find3 = items.find(function (item) {
        return item.value === selectedOption;
      })) === null || _items$find3 === void 0 ? void 0 : _items$find3.items.map(function (item) {
        var isSelected = values.includes(item.value);
        return /*#__PURE__*/_jsx(DropdownBaseItem, {
          value: item.value,
          searchKey: item.label,
          isSelected: isSelected,
          onSelect: function onSelect() {
            return handleSelection(item.value, !isSelected);
          },
          className: cn(isSelected && 'tw-bg-neutral-light-300 dark:tw-bg-neutral-dark-600', className),
          children: item.label
        }, item.value);
      })]
    }) : items.map(function (item) {
      var _items$find$items2, _items$find4;
      var categoryItems = (_items$find$items2 = (_items$find4 = items.find(function (cat) {
        return cat.value === item.value;
      })) === null || _items$find4 === void 0 ? void 0 : _items$find4.items) !== null && _items$find$items2 !== void 0 ? _items$find$items2 : [];
      var selectedCount = selection === null || selection === void 0 ? void 0 : selection.filter(function (select) {
        return categoryItems.find(function (item) {
          return item.value === select;
        });
      }).length;
      return /*#__PURE__*/_jsxs(CommandItem, {
        value: item.value.toString(),
        removeCheck: true,
        onSelect: function onSelect() {
          var _items$find5;
          setSelectedOption(item.value);
          var arrayFilter = (_items$find5 = items.find(function (it) {
            return it.value === item.value;
          })) === null || _items$find5 === void 0 ? void 0 : _items$find5.items;
          setItems(arrayFilter);
        },
        children: [/*#__PURE__*/_jsxs("div", {
          className: "tw-flex tw-items-center tw-gap-0.8",
          children: [item.icon && /*#__PURE__*/_jsx("div", {
            className: cn(iconVariants({
              variant: getParsedVariant({
                iconColorVariant: item.iconColorVariant,
                isSelected: false
              })
            })),
            children: item.icon({
              size: item.iconSize || 24,
              className: 'tw-text-inherit tw-flex-shrink-0'
            })
          }), item.label, selectedCount > 0 && /*#__PURE__*/_jsx("span", {
            className: "tw-rounded tw-flex tw-items-center tw-justify-center tw-w-2 tw-h-2 tw-font-body5 tw-bg-primary-light-500 tw-text-neutral-light-100",
            children: selectedCount
          })]
        }), _ArrowIosRight || (_ArrowIosRight = /*#__PURE__*/_jsx(ArrowIosRight, {
          size: 24,
          className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
        }))]
      }, item.value);
    })
  });
};